import { FormattedMessage } from 'react-intl';

import { FilterWidget } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import ShowDetails from '@sorare/marketplace/src/components/TokenPreview/ShowDetails';
import {
  AcceptedCurrencyGroup,
  CollectibleFilter,
  CollectibleGroup,
  GameweekFilter,
  LeagueFilter,
  NotInLineupFilter,
  PriceGroup,
  RarityGroup,
  RefineActiveLeague,
  RefineActiveNationalTeam,
  RefineAppearances,
  RefineAverageScore,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineNationality,
  RefineOnSale,
  RefinePlayer,
  RefineSeason,
  RefineSerialNumber,
  SaleGroup,
  filterSeparator,
} from '@sorare/marketplace/src/searchCards';

import FootballCustomDecksFilter from 'components/deck/FootballCustomDecksFilter';
import {
  RefineActiveClub,
  RefineClub,
} from 'components/searchCards/RefineClubs';
import RefineInSeason from 'components/searchCards/RefineInSeason';
import { RefineFootballPosition } from 'components/searchCards/RefinePosition';

const CardGroup = (props?: {
  withoutActiveLeague?: boolean;
  withoutTeam?: boolean;
  withoutCompetitionEligibility: boolean;
}) => ({
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    ...((props?.withoutActiveLeague && []) || [RefineActiveLeague]),
    ...((props?.withoutCompetitionEligibility && []) || [LeagueFilter]),
    RefineFootballPosition,
    RefineSeason,
    ...((props?.withoutTeam && []) || [RefineClub]),
  ].filter(Boolean),
});

export const MatchInfoGroup = {
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [GameweekFilter, RefineAverageScore(), RefineAppearances()],
};

const PlayerGroup = (props?: {
  withoutActiveClub?: boolean;
  withoutNationalTeam?: boolean;
}) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefineCardPlayerAge,
    ...((props?.withoutNationalTeam && []) || [RefineNationality]),
    RefinePlayer,
    ...((props?.withoutActiveClub && []) || [RefineActiveClub]),
    ...((props?.withoutNationalTeam && []) || [RefineActiveNationalTeam]),
  ].filter(Boolean),
});

const ToggleGroup = (additionalFilters?: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    RefineInSeason,
    ...(additionalFilters?.flatMap(filter => [filterSeparator, filter]) || []),
  ],
});

export const primaryMarketFilters = [
  RarityGroup,
  PriceGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition]),
  SaleGroup,
];

export const instantBuyMarketFilters = [
  RarityGroup,
  PriceGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition]),
];

export const secondaryMarketFilters = [
  ToggleGroup(),
  RarityGroup,
  PriceGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter(),
  ]),
  AcceptedCurrencyGroup,
];

export const secondaryMarketStackedFilters = [
  PriceGroup,
  CardGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter(),
  ]),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = [
  ToggleGroup([RefineOnSale()]),
  RarityGroup,
  PriceGroup,
  CardGroup({ withoutCompetitionEligibility: true, withoutActiveLeague: true }),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter({ withLegend: false }),
  ]),
  AcceptedCurrencyGroup,
];

export const clubPageFilters = [
  ToggleGroup([RefineOnSale()]),
  RarityGroup,
  PriceGroup,
  CardGroup({ withoutCompetitionEligibility: true, withoutTeam: true }),
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter({ withLegend: false }),
  ]),
  AcceptedCurrencyGroup,
];

export const leaguePageFilters = [
  ToggleGroup([RefineOnSale()]),
  RarityGroup,
  PriceGroup,
  CardGroup({ withoutCompetitionEligibility: true }),
  MatchInfoGroup,
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter({ withLegend: true }),
  ]),
  AcceptedCurrencyGroup,
];

export const countryPageFilters = [
  ToggleGroup([RefineOnSale()]),
  RarityGroup,
  PriceGroup,
  CardGroup(),
  MatchInfoGroup,
  PlayerGroup({ withoutNationalTeam: true }),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter(),
  ]),
  AcceptedCurrencyGroup,
];

export const myClubFilters = (showDecksFilter: boolean) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    ToggleGroup([RefineOnSale(), NotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'deck', filters: [FootballCustomDecksFilter] },
    ]) ||
      []),
    RarityGroup,
    CardGroup(),
    MatchInfoGroup,
    PlayerGroup(),
    CollectibleGroup([
      RefineSerialNumber,
      RefineCardLevel,
      RefineCardEdition,
      CollectibleFilter(),
    ]),
    AcceptedCurrencyGroup,
  ].filter(Boolean);
